
import { Component, Prop, Watch } from 'vue-property-decorator'
import Axios from 'axios'
import { email, minLength, required, helpers } from 'vuelidate/lib/validators'
import { ANTIFORGERY_INPUT_NAME, BASE_URL } from '../helpers/constants'
import { ICartBE } from '../helpers/types'
import BasicShoppingCart from './basic-shopping-cart.vue'

const isBEPostCode = (value: string) => !helpers.req(value) || value.length === 4

@Component({
  validations: {
    cartDataBe: {
      ProjectInformation: {
        ProjectName: { required, minLength: minLength(1) },
        ProjectInfo: { required, minLength: minLength(1) },
        ContactPersonName: { required, minLength: minLength(1) },
        PhoneNumber: { required, minLength: minLength(1) },
        EmailAddress: { required, minLength: minLength(1), email },
        PostalCode: { isBEPostCode }
      },
      QuotationRequestData: {
        Organization: { required, minLength: minLength(1) }
      }
    },
    terms: { checked: val => val }
  }
})

export default class ShoppingCartBe extends BasicShoppingCart {
  @Prop() cartItems: any
  @Prop() cartId: any

  loading = false
  cartDataBe: ICartBE | null = null
  openCommentIndex: number | null = null
  shoppingCartComponentKey: 1
  minusActive = false
  plusActive = false

  terms = false
  files: any = []
  buttonLoading = false

  next(): void {
    this.transition = 'slide-from-right'
    if (this.isStepTwo) {
      this.$v.cartDataBe.ProjectInformation?.$touch()
      if (this.$v.cartDataBe.ProjectInformation?.$invalid) {
        this.$nextTick(() => this.scrollErrorInView('step-two'))
        return
      } else {
        this.enableStepThree = true
      }
    }
    else if (this.isStepThree){
      this.$v.cartDataBe.QuotationRequestData?.$touch()
      this.$v.terms?.$touch()
      if(this.$v.cartDataBe.QuotationRequestData?.$invalid || this.$v.terms?.$invalid){
        this.$nextTick(() => this.scrollErrorInView('step-three'))
      } else {
        this.finishOrder()
      }
      return
    }

    if(!this.confirmationStep) {
      this.activeStep++
      this.sendGaCheckoutEvent(this.activeStep + 1, this.cartId, this.cartItems)
    }
    setTimeout(() => window.scrollTo(0, 0), 1000)

  }

  async finishOrder(): Promise<void> {

    this.buttonLoading = true
    try {
      const antiForgeryInput: HTMLInputElement | null = document.querySelector(`input[name=${ANTIFORGERY_INPUT_NAME}]`)
      const bodyFormData = this.toFormData()
      if(antiForgeryInput) bodyFormData.set(ANTIFORGERY_INPUT_NAME, antiForgeryInput.value)

      await Axios.post(
        `${BASE_URL}/quoterequests/be/submit`,
        bodyFormData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      )

      this.$store.commit('cart/setItemsCount', 0)
      this.$store.commit('cart/setItems', [])
      this.$store.dispatch('cart/getShoppingCartCount')
      this.enabledConfirmation = true
      this.$store.commit('cart/clearCartData')
      this.activeStep++
      window.scrollTo(0, 0)
      this.sendGaCheckoutEvent(this.activeStep + 1, this.cartId, this.cartItems)
      this.sendGaPurchaseEvent(this.cartId, this.cartItems)
    } catch (error) {
      this.$store.commit('notifications/add', { message: this.$options?.filters?.t('sendCartError') })
    } finally {
      this.buttonLoading = false
    }
  }

  toFormData(): FormData {
    const bodyFormData = new FormData();

    const projectInfoKey = Object.keys(this.cartDataBe!.ProjectInformation)
    projectInfoKey.forEach(key => {
      const data = this.cartDataBe!.ProjectInformation as any
      bodyFormData.set('request.ProjectInformation.' + key, data[key])
    })

    const quotationReqKeys = Object.keys(this.cartDataBe!.QuotationRequestData)
    quotationReqKeys.forEach(key => {
      const data = this.cartDataBe!.QuotationRequestData as any
      bodyFormData.set('request.QuotationRequestData.' + key, data[key])
    })

    if (this.cartDataBe!.Attachments) {
      this.cartDataBe!.Attachments!.forEach((item, index) => {
        if (item.AttachmentData) {
          bodyFormData.set(`request.Attachments[${index}].AttachmentData`, item.AttachmentData)
        }
      })
    }

    return bodyFormData
  }

  async changeItemQuantity(event: any, value: any, item: any, index: number): Promise<void> {
    let quantity: any = null
    let salesUnit = null

    this.setButtonLoader(index)

    if(event === 'plus') {
      salesUnit = this.getSalesUnit(item)
      quantity = this.getCorrectAmount(parseFloat(value) + salesUnit, item)

      this.plusActive = true
    } else if (event === 'minus') {
      salesUnit = this.getSalesUnit(item)
      quantity = this.getCorrectAmount(parseFloat(value) - salesUnit, item)

      this.minusActive = true
    }  else {
      quantity = this.getCorrectAmount(event.target.value, item)
    }

    item.quantity = quantity

    try {
      const res = await Axios({
        method: 'put',
        url: `${BASE_URL}/cart/items`,
        data: {
          lineItemId: item.id,
          quantityDecimal: quantity
        }
      })
      if (res) {
        this.$store.commit('cart/setItemsCount', res.data.itemsCount)

        const tempItems = this.$store.state.cart.items
        tempItems.map((el: any) => {
          if (el.id === item.id) el.quantityDecimal = quantity
        })
        this.$store.commit('cart/setItems', tempItems)

      } else throw new Error()
    } catch (error) {
      this.$store.commit('notifications/add', { message: this.$options?.filters?.t('changeCartAmountMessage')})
    } finally {
      this.setRemoveItemLoader(null)
      this.minusActive = false
      this.plusActive = false
    }
  }

  async removeItem(): Promise<void> {
    const id = this.$store.state.cart.itemId
    const itemIndex = this.$store.state.cart.items.findIndex((el: any) => el.id ===  id)
    const productForGta = this.$store.state.cart.items[itemIndex]

    this.setRemoveItemLoader(itemIndex)

    try {
      const res = await Axios({
        method: 'DELETE',
        url: `${BASE_URL}/cart/items?lineItemId=${id}`
      })
      if (res) {
        this.$store.commit('cart/setItemsCount', res.data.itemsCount)
        this.sendGaRemoveFromCartEvent(productForGta)

        const tempItems = this.$store.state.cart.items
        tempItems.splice(itemIndex, 1)
        this.$store.commit('cart/setItems', tempItems)

      } else throw new Error()
    } catch (error) {
      this.$store.commit('notifications/add', { message: this.$options?.filters?.t('deleteFromCartErrorMessage') })
    } finally {
      this.$store.commit('cart/setItemId', null)

      this.setRemoveItemLoader(null)
    }
  }

  created() {
    this.$store.commit('cart/getFromStorageBe')
    this.cartDataBe = this.$store.state.cart.cartDataBe
    this.$store.commit('cart/setItems', this.cartItems)

    this.$nextTick(() => this.sendGaCheckoutEvent(this.activeStep + 1, this.cartId, this.cartItems))
  }

  @Watch('cartDataBe', { deep: true })
  onProjectInformationChange(cartData: any): void {
    this.$store.commit('cart/updateCartDataBe', cartData)

    if(this.$v.cartDataBe.ProjectInformation && !this.$v.cartDataBe.ProjectInformation.$invalid) {
      this.enableStepThree = true
    } else {
      this.enableStepThree = false
    }
  }

  @Watch('files', { deep: true })
  onAttachmentsChange(items: any): void {
    if (!this.cartDataBe) return

    if (items) {
      const files = [...items]

      this.cartDataBe.Attachments = files.map(obj => ({
        AttachmentData: obj
      }))
    } else this.cartDataBe.Attachments = []
  }
}
